import React, { useState } from "react";
import { Table, Text, Modal, Button } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import dayjs from "dayjs";
import { Trash, UserPlus } from "tabler-icons-react";
import { useQueryClient } from "@tanstack/react-query";

import { Organization } from "types";

import styles from "./OrgTable.module.css";
import UpdateOrgModal from "../YourOrgModal";
import { PermissionButton } from "api/permission/buttonpermission";
import { useDeleteOrganization } from "api/organizations/deleteOrg";

type OrgTableProps = {
  org: Organization;
};

export default function OrgTable({ org }: OrgTableProps) {
  const [opened, setOpened] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const queryClient = useQueryClient();
  const { mutate: deleteOrg, isLoading: isDeleting } = useDeleteOrganization();

  const handleEditClick = () => {
    setSelectedOrgId(org._id);
    setOpened(true);
  };

  const handleCloseModal = () => {
    setOpened(false);
    setSelectedOrgId(null);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpened(true);
  };

  const confirmDelete = () => {
    deleteOrg(org._id, {
      onSuccess: () => {
        setDeleteModalOpened(false);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryClient.invalidateQueries(["org"]);
      },
    });
  };

  return (
    <div className={styles.container}>
      <Table
        withBorder
        // className={styles.table}
        verticalSpacing="sm"
        horizontalSpacing={20}
      >
        <thead
          style={{
            backgroundColor: "#f5f5f5",
          }}
        >
          <tr>
            <th>NAME</th>
            <th>CREATED AT</th>
            <th>MEMBERS</th>
            {/* <th>PROJECTS</th> */}
            <th>HANDBOOK</th>
            <th className={styles.delete}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ fontSize: "13px" }}>{org.name}</td>
            <td style={{ fontSize: "13px" }}>
              {dayjs(org.createdAt).isValid()
                ? dayjs(org.createdAt).format("DD MMM YYYY")
                : "-"}
            </td>
            <td style={{ fontSize: "13px" }}>
              {(org as any)?.pagination?.total}
            </td>
            {/* <td style={{ fontSize: "13px" }}>{org.projects.length}</td> */}
            <td style={{ fontSize: "13px" }}>
              {org.handbook ? (
                <Text
                  component="a"
                  href={org.handbook}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue"
                  size="sm"
                  sx={{
                    maxWidth: 180,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View Handbook
                </Text>
              ) : (
                <Text size="sm" color="dimmed">
                  -
                </Text>
              )}
            </td>
            <td>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  whiteSpace: "nowrap",
                }}
              >
                <PermissionButton
                  color="red.8"
                  permission="adminSettings"
                  leftIcon={<Trash size={18} />}
                  style={{ minWidth: 210, paddingLeft: 12, paddingRight: 12 }}
                  onClick={handleDeleteClick}
                  loading={isDeleting}
                >
                  Delete Organization
                </PermissionButton>

                <PermissionButton
                  permission="organization_management.organizationUpdate"
                  style={{ minWidth: 210 }}
                  leftIcon={<UserPlus size={16} />}
                  onClick={handleEditClick}
                >
                  Edit Organizations
                </PermissionButton>
              </div>
            </td>
          </tr>
        </tbody>
      </Table>

      {selectedOrgId && (
        <UpdateOrgModal
          opened={opened}
          orgId={selectedOrgId}
          onClose={handleCloseModal}
        />
      )}

      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Delete Organization"
        centered
      >
        <Text size="sm" mb="md">
          Are you sure you want to delete &quot;{org.name}&quot;? This action
          cannot be undone.
        </Text>
        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
        >
          <Button
            variant="default"
            onClick={() => setDeleteModalOpened(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <PermissionButton
            color="red"
            onClick={confirmDelete}
            loading={isDeleting}
            permission="adminSettings"
          >
            Delete
          </PermissionButton>
        </div>
      </Modal>
    </div>
  );
}
